import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";
import { toast } from "sonner";
import { ShieldSlashIcon as ShieldSlash, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";

const SUGGESTED_REASONS = [
  "Falsified pedigree records",
  "Fraudulent ownership transfer",
  "Harassment of other members",
  "Repeated policy violations",
  "Suspected fraudulent payments",
];

/**
 * Admin dialog to disable (suspend) or reinstate a user account, with a
 * mandatory reason for suspensions. The reason flows into the user's
 * inbox + email + login error screen, plus a permanent audit log row.
 */
export default function SuspendUserDialog({ open, onClose, user, onDone }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isReinstating = !!user?.suspended;

  const close = () => {
    if (submitting) return;
    setReason("");
    onClose();
  };

  const submit = async () => {
    if (!isReinstating && !reason.trim()) {
      toast.error("A reason is required when suspending an account");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/admin/data/users/${user.id}/suspend`, {
        suspended: !isReinstating,
        reason: isReinstating ? "" : reason.trim(),
      });
      toast.success(isReinstating ? "Account reinstated" : "Account suspended");
      setReason("");
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-lg" data-testid="suspend-user-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isReinstating ? (
              <>
                <CheckCircle size={18} weight="duotone" className="text-ink" />
                Reinstate {user.email}?
              </>
            ) : (
              <>
                <ShieldSlash size={18} weight="duotone" className="text-crimson" />
                Suspend {user.email}?
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isReinstating
              ? "The user will be able to sign in again. Their inbox + email will be notified."
              : "The user will be blocked from every authenticated request and shown the reason on the login screen, in their inbox, and via email. A permanent audit row is recorded."}
          </DialogDescription>
        </DialogHeader>

        {!isReinstating && (
          <>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
                Reason for suspension *
              </Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                maxLength={1000}
                placeholder="Describe the violation or rule break in plain English. The user will see this verbatim."
                className="mt-1 rounded-lg"
                data-testid="suspend-user-reason"
              />
              <p className="mt-1 text-[11px] text-[#6B6B6B] text-right">
                {reason.length}/1000
              </p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">
                Quick reasons
              </p>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_REASONS.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setReason(r)}
                    className="text-xs rounded-full border border-[#E5E5E5] bg-white text-[#6B6B6B] hover:border-[#C1272D] hover:text-crimson px-3 py-1.5 transition-colors"
                    data-testid={`suspend-quick-${r.toLowerCase().split(" ")[0]}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {isReinstating && user.suspension_reason && (
          <div className="bg-[#FBF7EC] border border-[#E5D08A] rounded-xl p-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A6A00]">
              Original reason
              {user.suspended_by_name && (
                <span className="font-normal normal-case ml-1">· {user.suspended_by_name}</span>
              )}
            </p>
            <p className="text-sm text-ink mt-1 whitespace-pre-wrap">
              {user.suspension_reason}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close} className="rounded-full">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className={
              isReinstating
                ? "rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
                : "rounded-full bg-[#C1272D] hover:bg-[#0E0E0E] text-white"
            }
            data-testid="suspend-user-submit"
          >
            {submitting
              ? (isReinstating ? "Reinstating…" : "Suspending…")
              : (isReinstating ? "Reinstate account" : "Suspend account")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
